import type { ApolloClient } from '@apollo/client';
import { from, map } from 'rxjs';
import { useFragment as getFragmentData } from '@/graphql';
import {
    DataType,
    MeasuringPointCommentFieldsFragmentDoc,
    type MeasuringPointCommentFieldsFragment,
    type MeasuringPointCommentsQuery,
    type MeasuringPointCommentsQueryVariables,
} from '@/graphql/types';
import { getDataTypeKeyByValue } from '../../../../../../graph/data-types';
import { MEASURING_POINT_COMMENTS_QUERY } from '../../../../../../graph/graph-elements/comments-plugin/queries';
import type { RuntimeDataServiceInput, RuntimeDataServiceOutput } from '../contracts';
import {
    RuntimeDataService,
    type RuntimeDataServiceRequest,
    type RuntimeResultLinkDescriptor,
    type RuntimeTabularCellRenderOptions,
    type RuntimeTabularColumnDescriptor,
} from '../types';
import {
    isRecord,
    normalizeRequiredId,
    normalizeRequiredFiniteNumber,
    normalizeNullableFiniteNumber,
    normalizeString,
    normalizeArrayEntries,
} from '../validationPrimitives';
import type { RenderContext, RenderRuntimeGraphSettings } from '../../../elements/types';
import type { TableLayoutShape } from '../../../elements/table/contracts';
import { format } from '../../../../../../utils/formatting';
import { buildGroupedRowsFromColumns } from '../tabular';

type CommentEntry = RuntimeDataServiceOutput<'measuring_point.comments'>['entries'][number];
type CommentReply = CommentEntry['replies'][number];
/**
 * One line of a thread: the comment itself or one of its replies. `thread` identifies the
 * parent and is rendered once, spanning the thread's rows.
 */
type CommentThreadRow = Pick<CommentEntry, 'createdAt' | 'text' | 'username'> & {
    threadStartTime: number;
    threadEndTime: number | null;
};
type RawCommentNode = NonNullable<
    NonNullable<MeasuringPointCommentsQuery['measuringPointComments']>[number]
>;
type ApiCommentThread = {
    comment: MeasuringPointCommentFieldsFragment;
    replies: readonly MeasuringPointCommentFieldsFragment[];
};

const DATA_TYPE_VALUES = new Set<string>(Object.values(DataType));

function toGraphqlDataType(dataType: RenderRuntimeGraphSettings['dataType']): DataType {
    const graphqlDataType = getDataTypeKeyByValue(dataType);
    if (!graphqlDataType) {
        throw new Error(`Unknown graph data type "${dataType}" while resolving comments.`);
    }

    return graphqlDataType;
}

function normalizeCommentReply(value: unknown, pathLabel: string): CommentReply {
    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object.`);
    }

    return {
        createdAt: normalizeRequiredFiniteNumber(value.createdAt, `${pathLabel}.createdAt`),
        text: normalizeString(value.text, `${pathLabel}.text`),
        username: normalizeString(value.username, `${pathLabel}.username`),
    };
}

function normalizeCommentEntry(value: unknown, pathLabel: string): CommentEntry {
    if (!isRecord(value)) {
        throw new Error(`Expected "${pathLabel}" to be an object.`);
    }

    return {
        startTime: normalizeRequiredFiniteNumber(value.startTime, `${pathLabel}.startTime`),
        endTime: normalizeNullableFiniteNumber(value.endTime, `${pathLabel}.endTime`),
        createdAt: normalizeRequiredFiniteNumber(value.createdAt, `${pathLabel}.createdAt`),
        text: normalizeString(value.text, `${pathLabel}.text`),
        username: normalizeString(value.username, `${pathLabel}.username`),
        replies: normalizeArrayEntries(
            value.replies,
            `${pathLabel}.replies`,
            normalizeCommentReply,
            { allowNullish: true }
        ),
    };
}

function readCommentNode(node: RawCommentNode): {
    comment: MeasuringPointCommentFieldsFragment;
    replies: readonly MeasuringPointCommentFieldsFragment[];
} {
    return {
        comment: getFragmentData(MeasuringPointCommentFieldsFragmentDoc, node),
        replies: getFragmentData(MeasuringPointCommentFieldsFragmentDoc, node.replies ?? []),
    };
}

function normalizeDateTimeMs(value: unknown, pathLabel: string): number {
    const timestamp = new Date(normalizeString(value, pathLabel)).getTime();
    return normalizeRequiredFiniteNumber(timestamp, pathLabel);
}

function normalizeApiCommentReply(
    reply: MeasuringPointCommentFieldsFragment,
    pathLabel: string
): CommentReply {
    return {
        createdAt: normalizeDateTimeMs(reply.createdAt, `${pathLabel}.createdAt`),
        text: normalizeString(reply.text, `${pathLabel}.text`),
        username: normalizeString(reply.user?.username, `${pathLabel}.user.username`),
    };
}

function normalizeApiCommentEntry(
    thread: ApiCommentThread,
    input: RuntimeDataServiceInput<'measuring_point.comments'>,
    pathLabel: string
): CommentEntry | null {
    const { comment, replies } = thread;

    if (comment.dataType !== input.dataType) {
        return null;
    }

    const startTime = normalizeRequiredFiniteNumber(comment.startTime, `${pathLabel}.startTime`);
    const endTime = normalizeNullableFiniteNumber(comment.endTime, `${pathLabel}.endTime`);

    if (startTime > input.endTime || (endTime ?? startTime) < input.startTime) {
        return null;
    }

    return {
        startTime,
        endTime,
        createdAt: normalizeDateTimeMs(comment.createdAt, `${pathLabel}.createdAt`),
        text: normalizeString(comment.text, `${pathLabel}.text`),
        username: normalizeString(comment.user?.username, `${pathLabel}.user.username`),
        replies: replies.map((reply, index) =>
            normalizeApiCommentReply(reply, `${pathLabel}.replies[${index}]`)
        ),
    };
}

function validateCommentsInput(
    input: unknown
): RuntimeDataServiceInput<'measuring_point.comments'> {
    if (!isRecord(input)) {
        throw new Error('Expected "measuring_point.comments" input to be an object.');
    }

    const startTime = normalizeRequiredFiniteNumber(
        input.startTime,
        'measuring_point.comments.startTime'
    );
    const endTime = normalizeRequiredFiniteNumber(
        input.endTime,
        'measuring_point.comments.endTime'
    );
    const dataType = normalizeString(input.dataType, 'measuring_point.comments.dataType');

    if (!DATA_TYPE_VALUES.has(dataType)) {
        throw new Error('Expected "measuring_point.comments.dataType" to be a valid data type.');
    }

    return {
        measuringPointId: normalizeRequiredId(
            input.measuringPointId,
            'measuring_point.comments.measuringPointId'
        ),
        startTime,
        endTime,
        dataType: dataType as DataType,
    };
}

function validateCommentsResult(
    result: unknown
): RuntimeDataServiceOutput<'measuring_point.comments'> {
    if (!isRecord(result)) {
        throw new Error('Expected "measuring_point.comments" result to be an object.');
    }

    return {
        entries: normalizeArrayEntries(
            result.entries,
            'measuring_point.comments.entries',
            normalizeCommentEntry
        ),
    };
}

function parseCommentsEntriesLinkValue(
    value: unknown
): RuntimeDataServiceOutput<'measuring_point.comments'>['entries'] {
    return validateCommentsResult({ entries: value }).entries;
}

function formatCommentTs(ts: number, timezone: string): string {
    return format(ts, { timezone, hideSeconds: true });
}

function createCommentThreadRows(entry: CommentEntry): CommentThreadRow[] {
    const thread = {
        threadStartTime: entry.startTime,
        threadEndTime: entry.endTime,
    };

    return [
        { ...thread, createdAt: entry.createdAt, username: entry.username, text: entry.text },
        ...entry.replies.map((reply) => ({
            ...thread,
            createdAt: reply.createdAt,
            username: reply.username,
            text: reply.text,
        })),
    ];
}

function buildCommentThreadGroups(entries: readonly CommentEntry[]): CommentThreadRow[][] {
    return entries.map(createCommentThreadRows);
}

const COMMENTS_COLUMNS = [
    {
        kind: 'timeRange',
        key: 'thread',
        label: gettext('Date/Time'),
        defaultAlignment: 'left' as const,
        // A thread that is still open has no end, which the legacy report also rendered as a
        // window starting and ending at the same moment.
        resolveCell: (row: CommentThreadRow) => ({
            from: row.threadStartTime,
            to: row.threadEndTime ?? row.threadStartTime,
        }),
    },
    {
        kind: 'text',
        key: 'createdAtAndUsername',
        requiresTimezone: true,
        label: gettext('REPORT_COMMENT_DATE_NAME'),
        defaultAlignment: 'left' as const,
        resolveCell: (row: CommentThreadRow, options) =>
            `${formatCommentTs(row.createdAt, options.timezone)} - ${row.username}`,
    },
    {
        kind: 'text',
        key: 'text',
        label: gettext('REPORT_COMMENT_MESSAGE'),
        defaultAlignment: 'left' as const,
        resolveCell: (row: CommentThreadRow) => row.text,
    },
] as const satisfies readonly RuntimeTabularColumnDescriptor<CommentThreadRow>[];

function buildCommentsTableShape(
    settings: TableLayoutShape,
    entries: RuntimeDataServiceOutput<'measuring_point.comments'>['entries'],
    context: RenderContext,
    options: RuntimeTabularCellRenderOptions
): TableLayoutShape {
    if (context.kind !== 'measuring-point') {
        throw new Error('Missing measuring-point context for comments entries.');
    }

    return {
        header: settings.header,
        rows: buildGroupedRowsFromColumns(
            buildCommentThreadGroups(entries),
            COMMENTS_COLUMNS,
            'thread',
            options
        ),
        columnAlignments: settings.columnAlignments,
        columnWidths: settings.columnWidths,
    };
}

export class CommentsRuntimeDataService extends RuntimeDataService<'measuring_point.comments'> {
    static readonly serviceId = 'measuring_point.comments' as const;

    static get serviceMetadata() {
        return {
            label: gettext('DATA_SERVICE_COMMENTS_LABEL'),
            description: gettext('DATA_SERVICE_COMMENTS_DESCRIPTION'),
        } as const;
    }

    static get serviceResultLinks() {
        return [
            {
                key: 'entries',
                label: gettext('DATA_SERVICE_COMMENTS_ENTRIES_LABEL'),
                description: gettext('DATA_SERVICE_COMMENTS_ENTRIES_DESCRIPTION'),
                supportedContextKinds: ['measuring-point'],
                supportedSwarmTypes: null,
                path: 'entries',
                valueKind: 'tabular',
                argPolicy: 'forbidden',
                exposeInInsertionPresets: true,
                columns: COMMENTS_COLUMNS,
                parseTableRows: (linkValue) =>
                    buildCommentThreadGroups(parseCommentsEntriesLinkValue(linkValue)).flat(),
                buildTableShape: (settings, linkValue, context, options) =>
                    buildCommentsTableShape(
                        settings,
                        parseCommentsEntriesLinkValue(linkValue),
                        context,
                        options
                    ),
                buildInput: ({ context, graphTimeWindow, graphSettings }) => {
                    if (!context || context.kind !== 'measuring-point') {
                        throw new Error('Comments entries require measuring-point context.');
                    }

                    if (!graphTimeWindow || !graphSettings) {
                        throw new Error('Graph runtime settings are required to resolve comments.');
                    }

                    return {
                        measuringPointId: context.measuringPoint.id,
                        startTime: graphTimeWindow.viewStartTs,
                        endTime: graphTimeWindow.viewEndTs,
                        dataType: toGraphqlDataType(graphSettings.dataType),
                    };
                },
            },
        ] as const satisfies readonly RuntimeResultLinkDescriptor<'measuring_point.comments'>[];
    }

    constructor(private readonly client: ApolloClient<object>) {
        super();
    }

    validateArgs = validateCommentsInput;

    validateResult = validateCommentsResult;

    // The backend query returns all comments for the measuring point — no time-range filter.
    // We filter client-side to match legacy commentsInView$ behaviour (filterWithinView).
    createResult$ = ({ input }: RuntimeDataServiceRequest<'measuring_point.comments'>) =>
        from(
            this.client.query<MeasuringPointCommentsQuery, MeasuringPointCommentsQueryVariables>({
                query: MEASURING_POINT_COMMENTS_QUERY,
                variables: { measuringPointId: input.measuringPointId, withReplies: true },
                fetchPolicy: 'cache-first',
            })
        ).pipe(
            map(({ data }) => {
                if (!data?.measuringPointComments) {
                    throw new Error(
                        'Missing measuringPointComments payload while resolving runtime comments data.'
                    );
                }

                const entries = data.measuringPointComments
                    .filter((commentNode): commentNode is RawCommentNode => commentNode !== null)
                    .map(readCommentNode)
                    .map((thread, index) =>
                        normalizeApiCommentEntry(thread, input, `measuringPointComments[${index}]`)
                    )
                    .filter((entry): entry is CommentEntry => entry !== null)
                    .sort((a, b) => a.startTime - b.startTime);

                return validateCommentsResult({ entries });
            })
        );
}

export function createCommentsService(client: ApolloClient<object>): CommentsRuntimeDataService {
    return new CommentsRuntimeDataService(client);
}
